import { useState, useRef } from 'react';
import Button from '@mui/material/Button';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

import { DrawerItem } from 'helpers/drawer-config';
import { useGlobalContext } from 'context/GlobalContext';

import NavBarDropdown from './NavBarDropdown';

type NavBarItemProps = {
  item: DrawerItem;
};

/**
 * NavBarItem Component
 *
 * Item individual del menú de navegación superior.
 * Muestra un botón que al hacer hover o click abre un dropdown con submenús.
 *
 * @component
 * @param {NavBarItemProps} props - Las propiedades del componente
 * @returns {JSX.Element | null} El item de navegación o null si no tiene permisos
 */
function NavBarItem({ item }: NavBarItemProps): JSX.Element | null {
  const { hasPerm } = useGlobalContext();
  const { t } = useTranslation('global');
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Verificar permisos
  if (!hasPerm(item.permissions)) {
    return null;
  }

  const hasSubmenu = item.submenu && item.submenu.length > 0;
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (hasSubmenu) {
      setAnchorEl(event.currentTarget);
    } else if (item.path) {
      navigate(item.path);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        onClick={handleClick}
        // onMouseEnter={(e) => hasSubmenu && setAnchorEl(e.currentTarget)}
        ref={buttonRef}
        sx={{
          color: '#0E335C',
          textTransform: 'none',
          fontSize: '14px',
          fontWeight: 500,
          px: 2,
          py: 1,
          borderRadius: '4px',
          minWidth: 'auto',
          whiteSpace: 'nowrap',
          '&:hover': {
            backgroundColor: '#1976d2',
            color: '#FFFFFF'
          },
          ...(open && {
            backgroundColor: '#1976d2',
            color: '#FFFFFF'
          })
        }}
      >
        {t(item?.key)}
      </Button>

      {hasSubmenu && (
        <NavBarDropdown
          anchorEl={anchorEl}
          onClose={handleClose}
          open={open}
          submenu={item.submenu}
        />
      )}
    </>
  );
}

export default NavBarItem;
